import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {AsaxPropertiesService} from 'asax-properties';
import {AsaxElasticService} from 'asax-elastic';
import {Energy, Observation, PreviewsCollection} from 'asax-common';

@Injectable({
  providedIn: 'root'
})
export class CellPreviewsService {
  private filesCache = new Map<string, string>();

  constructor(private readonly asaxPropertiesService: AsaxPropertiesService,
              private readonly asaxElasticService: AsaxElasticService,
              private readonly httpClient: HttpClient) {
  }

  fetchPreviewsCollectionsForObservation(datasetId: string): Observable<PreviewsCollection[]> {
    return this.asaxElasticService.searchByQuery({
      index: 'observations',
      body: {
        _source: ['band', 'continuumSensitivity', 'array', 'energy', 'productFiles'],
        query: {terms: {_id: [datasetId]}}
      }
    }).pipe(
      map((data: any) => data.hits.hits.map(({_source}) => ({..._source}))),
      map((observations: Observation[]) => mapObservationToPreviewsCollections(observations[0])),
      catchError(() => [])
    );
  }

  getFile(file: string): string {
    return this.filesCache.get(file);
  }

  loadFile(file: string): void {
    this.httpClient.get(`${this.asaxPropertiesService.getProperties().dataPortalUrl}/${file}`, {responseType: 'blob'}).pipe(
      tap(image => this.addFileToCache(file, image)),
      switchMap(() => this.filesCache.get(file))
    ).subscribe({
      error: (e) => this.filesCache.set(file, 'error fetching file')
    });
  }

  fetchCartaSocketUrl(file: string): Observable<string> {
    const requestHandlerUrl = this.asaxPropertiesService.getProperties().requestHandlerUrl;
    return this.httpClient.get(`${requestHandlerUrl}/carta?file=${file}`, {responseType: 'text'});
  }

  private addFileToCache(file: string, image: Blob): void {
    const reader = new FileReader();
    reader.addEventListener('load', () => this.filesCache.set(file, URL.createObjectURL(image)));
    reader.readAsDataURL(image);
  }
}

const buildDistinctCollections = (energy: Energy[]): string[] => {
  const collections = new Set<string>(
    energy.map(({productFiles}) => productFiles || [])
      .flat()
      .map(({collection}) => collection)
      .filter(((collection) => collection !== 'ALMA'))
  );
  return ['ALMA', ...collections];
};

const mapObservationToPreviewsCollections = (observation: Observation): PreviewsCollection[] => {
  return buildDistinctCollections(observation.energy).map(collection => {
    const readmeFiles = observation.productFiles?.filter(file => file.collection === collection && file.fileClass === 'readme')?.map(file => file.ngasFileId);
    const qa2Files = observation.productFiles?.filter(file => file.collection === collection && file.fileClass === 'qa2')?.map(file => file.ngasFileId);
    const weblogFiles = observation.productFiles?.filter(file => file.collection === collection && file.fileClass === 'weblog')?.map(file => file.ngasFileId);

    const previews = observation.energy.map((energyRow, index) => {
      const {band, continuumSensitivity, array} = observation;
      const {chaNum, freqMin, freqMax, resMax, sens10, sensBand, polProduct} = energyRow;

      const imageFile = energyRow.productFiles?.find(file => file.collection === collection && file.fileClass === 'image')?.ngasFileId;
      const thumbnailFile = energyRow.productFiles?.find(file => file.collection === collection && file.fileClass === 'thumbnail')?.ngasFileId;
      const fitsFile = energyRow.productFiles?.find(file => file.collection === collection && file.fileClass === 'science')?.ngasFileId;
      const previewFile = energyRow.productFiles?.find(file => file.collection === collection && file.fileClass === 'preview')?.ngasFileId;
      const fitsFileSize = energyRow.productFiles?.find(file => file.collection === collection && file.fileClass === 'science')?.size;

      return {
        band,
        chaNum,
        polProduct,
        array,
        continuumSensitivity,
        freqMin,
        freqMax,
        resMax,
        sens10,
        sensBand,
        imageFile,
        thumbnailFile,
        fitsFile,
        previewFile,
        fitsFileSize,
        index
      };
    });

    return {name: collection, previews, readmeFiles, qa2Files, weblogFiles};
  });
};
